@if (offer(); as o) {
  @if (layout() === 'tiles') {
    <!-- TILES layout: catalog-style card (image on top, details below). Shares the same
         interaction handlers / outputs as the list variant — only the arrangement differs. -->
    <div
      class="offer-list-card offer-list-card--tiles"
      [class.offer-list-card--in-stock]="o.inStock"
      [class.offer-list-card--discount]="o.discount"
      [title]="
        (orderingEnabled() && o.isOrderable
          ? 'public.checkout.addToClick'
          : 'public.checkout.clickToDetails'
        ) | translate
      "
      (click)="currentCartStatus() === 'LOADED' && onRowClick()"
      (mousedown)="onMouseDown()"
      (mouseup)="onMouseUp()"
      (mouseleave)="onMouseLeave()"
      (touchstart)="onTouchStart($event)"
      (touchend)="onTouchEnd($event)"
      (touchcancel)="onTouchCancel($event)"
    >
      <div
        class="offer-list-card__tile"
        [class.offer-list-card__tile--out-of-stock]="!o.inStock"
      >
        <!-- Image with overlaid quick-add stepper (orderable) or info icon (needs details) -->
        <div
          class="offer-list-card__tile-image"
          [style.background-image]="getBackgroundImage(o.standardImage)"
          (click)="onImageClick($event)"
        >
          <div class="offer-list-card__tile-action">
            @if (o.isOrderable) {
              <app-offer-actions
                [offer]="o"
                [cartItems]="cartItems()"
                [currentCartStatus]="currentCartStatus()"
                [itemAddedToCart]="_itemAddedToCart"
                [decreaseItemQty]="_decreaseItemQty"
                [basketEnabled]="basketEnabled()"
                [orderingEnabled]="orderingEnabled()"
                [orderCaptureEnabled]="orderCaptureEnabled()"
                [isAdminOrderCaptureUpdate]="isAdminOrderCaptureUpdate()"
                (productDetailsViewed)="
                  onOfferActionsProductDetailsViewed($event)
                "
              ></app-offer-actions>
            } @else {
              <i
                class="fa fa-solid fa-info offer-list-card__info-icon container-theme-light-icons"
                [title]="'public.checkout.clickToDetails' | translate"
                placement="top"
                (click)="onInfoIconClick($event)"
              ></i>
            }
          </div>
        </div>

        <!-- Details: calories / name / description / price / allergens -->
        <div class="offer-list-card__tile-body text-unselectable">
          @if (getCalories(o.attributeDtos)) {
            <span class="offer-list-card__tile-calories">
              <img
                src="/assets/img/offer-characteristics/Customer/fire.png"
                alt=""
              />
              {{ getCalories(o.attributeDtos) }} kcal
            </span>
          }
          <div
            class="offer-list-card__tile-name font-size-primary theme-color-subtitle-2 font-weight-bold"
            [innerHTML]="o.name | richText | highlightSearch: searchTerm()"
          ></div>
          @if (o.shortDescription) {
            <div
              class="offer-list-card__tile-description font-size-secondary text-break"
              [innerHTML]="o.shortDescription | richText"
            ></div>
          }
          <app-offer-price
            [menuItem]="o"
            [locale]="locale()"
            [currency]="currency()"
            [currencySymbol]="currencySymbol()"
            [isAdminOrderCaptureUpdate]="isAdminOrderCaptureUpdate()"
          ></app-offer-price>
          <app-allergens
            [attributes]="o.attributeDtos"
            [isAdminOrderCaptureUpdate]="false"
          ></app-allergens>
        </div>
      </div>
    </div>
  } @else {
    <div
      class="offer-list-card"
      [class.offer-list-card--in-stock]="o.inStock"
      [class.offer-list-card--discount]="o.discount"
      [title]="
        (orderingEnabled() && o.isOrderable
          ? 'public.checkout.addToClick'
          : 'public.checkout.clickToDetails'
        ) | translate
      "
      (click)="currentCartStatus() === 'LOADED' && onRowClick()"
      (mousedown)="onMouseDown()"
      (mouseup)="onMouseUp()"
      (mouseleave)="onMouseLeave()"
      (touchstart)="onTouchStart($event)"
      (touchend)="onTouchEnd($event)"
      (touchcancel)="onTouchCancel($event)"
    >
      <div
        class="offer-list-card__inner"
        [class.offer-list-card__inner--out-of-stock]="!o.inStock"
      >
        <!-- In-cart accent stripe: mirrors catalog-layout-list .in-cart -->
        @if (cartItems().length > 0 && orderingEnabled()) {
          <div class="offer-list-card__in-cart" aria-hidden="true"></div>
        }

        <!-- Text body: name / description / price / allergens -->
        <div
          class="offer-list-card__body text-unselectable"
          [class.offer-list-card__body--with-image]="
            o.standardImage && !isAdminOrderCaptureUpdate()
          "
          [title]="
            (orderingEnabled() && o.isOrderable
              ? 'public.checkout.addToClick'
              : 'public.checkout.clickToDetails'
            ) | translate
          "
        >
          <!-- Name row (includes inline calories when there IS an image) -->
          <div
            class="offer-list-card__name font-size-primary"
            [class.font-weight-bold]="!isAdminOrderCaptureUpdate()"
          >
            @if (isAdminOrderCaptureUpdate()) {
              <span
                class="theme-color-subtitle-2 text-left"
                [innerHtml]="
                  (o.name?.length > 30 ? (o.name | slice: 0 : 30) : o.name)
                    | highlightSearch: searchTerm()
                "
              ></span>
            } @else {
              <span
                class="theme-color-subtitle-2 text-left"
                [innerHtml]="o.name | richText | highlightSearch: searchTerm()"
              ></span>
            }

            <!-- Calories shown next to name when there IS an image (hidden when no image) -->
            @if (
              getCalories(o.attributeDtos) &&
              !isAdminOrderCaptureUpdate() &&
              o.standardImage
            ) {
              <span
                class="offer-list-card__calories offer-list-card__calories--with-image text-right"
              >
                <img
                  src="/assets/img/offer-characteristics/Customer/fire.png"
                  alt=""
                />
                {{ getCalories(o.attributeDtos) }} kcal
              </span>
            }
          </div>

          <!-- Description (hidden in admin mode) -->
          @if (o.shortDescription && !isAdminOrderCaptureUpdate()) {
            <div
              class="offer-list-card__description font-size-secondary text-break"
              [innerHTML]="o.shortDescription | richText"
            ></div>
          }

          <!-- Price -->
          <app-offer-price
            [menuItem]="o"
            [locale]="locale()"
            [currency]="currency()"
            [currencySymbol]="currencySymbol()"
            [isAdminOrderCaptureUpdate]="isAdminOrderCaptureUpdate()"
          ></app-offer-price>

          <!-- Allergens (hidden in admin mode) -->
          @if (!isAdminOrderCaptureUpdate()) {
            <app-allergens
              [attributes]="o.attributeDtos"
              [isAdminOrderCaptureUpdate]="false"
            ></app-allergens>
          }
        </div>

        <!-- Media: calories (no-image variant) + image tile with overlaid actions -->
        <div class="offer-list-card__media">
          <!-- Calories shown separately when there is NO image (hidden when image present) -->
          @if (
            getCalories(o.attributeDtos) &&
            !isAdminOrderCaptureUpdate() &&
            !o.standardImage
          ) {
            <span
              class="offer-list-card__calories offer-list-card__calories--no-image"
            >
              <img
                src="/assets/img/offer-characteristics/Customer/fire.png"
                alt=""
              />
              {{ getCalories(o.attributeDtos) }} kcal
            </span>
          }

          <!-- Image tile (or plain action area when no image) -->
          <div
            class="offer-list-card__image-tile p-1"
            [style.background-image]="
              isAdminOrderCaptureUpdate()
                ? ''
                : getBackgroundImage(o.standardImage)
            "
            [class.offer-list-card__image-tile--has-image]="
              o.standardImage && !isAdminOrderCaptureUpdate()
            "
            [class.offer-list-card__image-tile--no-image-calories]="
              !o.standardImage && getCalories(o.attributeDtos)
            "
            (click)="onImageClick($event)"
            [title]="
              (orderingEnabled() && o.isOrderable
                ? 'public.checkout.addToClick'
                : 'public.checkout.clickToDetails'
              ) | translate
            "
          >
            @if (o.isOrderable) {
              <app-offer-actions
                [offer]="o"
                [cartItems]="cartItems()"
                [currentCartStatus]="currentCartStatus()"
                [itemAddedToCart]="_itemAddedToCart"
                [decreaseItemQty]="_decreaseItemQty"
                [basketEnabled]="basketEnabled()"
                [orderingEnabled]="orderingEnabled()"
                [orderCaptureEnabled]="orderCaptureEnabled()"
                [size]="'small'"
                [isAdminOrderCaptureUpdate]="isAdminOrderCaptureUpdate()"
                (productDetailsViewed)="
                  onOfferActionsProductDetailsViewed($event)
                "
              ></app-offer-actions>
            } @else {
              <i
                class="fa fa-solid fa-info offer-list-card__info-icon container-theme-light-icons"
                [title]="'public.checkout.clickToDetails' | translate"
                placement="top"
                (click)="onInfoIconClick($event)"
              ></i>
            }
          </div>
        </div>
      </div>
    </div>
  }
}
